/**
 * Lo que el panel de metricas ENSEÑA, en castellano. Solo presentacion.
 *
 * Los valores que viajan (resultado de una llamada, estado de una reunion,
 * motivo de un bloque sin datos, etiqueta del trimestre) siguen siendo los del
 * servidor: se comparan, se mandan a la API y deciden colores. Aqui solo se
 * traducen en el momento de pintarlos, y lo que no se conoce se pinta tal cual
 * llega: un valor nuevo del servidor se ve, no desaparece ni se inventa.
 *
 * Los nombres de etapa del embudo NO pasan por aqui: son los de la cuenta de
 * GHL de cada cliente y se pintan como el cliente los escribio.
 */

const RESULTADOS: Record<string, string> = {
  // Disposiciones del clasificador (apps/toolkit-server/src/calls/classify.ts).
  'Demo Scheduled': 'Demo agendada',
  'Connected Positive': 'Conectada positiva',
  'Connected Negative': 'Conectada negativa',
  'Connected Busy': 'Conectada ocupada',
  'No Answer': 'Sin respuesta',
  'Busy': 'Ocupado',
  'Wrong Number': 'Número equivocado',
  'Voicemail': 'Buzón de voz',
  'Gatekeeper': 'Filtro (recepción)',
  // Resultados en minuscula del mismo clasificador.
  meeting_booked: 'Reunión agendada',
  connected_positive: 'Conectada positiva',
  connected_negative: 'Conectada negativa',
  call_back_later: 'Llamar más tarde',
  not_interested: 'No le interesa',
  no_answer: 'Sin respuesta',
  busy: 'Ocupado',
  wrong_number: 'Número equivocado',
  voicemail: 'Buzón de voz',
  gatekeeper: 'Filtro (recepción)',
  unknown: 'Desconocido',
};

/** Resultado de una llamada para pintar. Uno desconocido se pinta tal cual llega. */
export function etiquetaResultado(resultado: string): string {
  return Object.prototype.hasOwnProperty.call(RESULTADOS, resultado) ? RESULTADOS[resultado]! : resultado;
}

const ESTADOS_REUNION: Record<string, string> = {
  held: 'celebrada',
  scheduled: 'agendada',
  noshow: 'no presentada',
  cancelled: 'cancelada',
  unknown: 'desconocido',
};

/** Estado de una reunion para pintar. Uno desconocido se pinta tal cual llega. */
export function etiquetaEstadoReunion(estado: string): string {
  return Object.prototype.hasOwnProperty.call(ESTADOS_REUNION, estado) ? ESTADOS_REUNION[estado]! : estado;
}

const ESTADOS_MENSAJE: Record<string, string> = {
  pending: 'pendiente',
  scheduled: 'programado',
  sent: 'enviado',
  delivered: 'entregado',
  read: 'leído',
  opened: 'abierto',
  undelivered: 'no entregado',
  failed: 'fallido',
};

/** Estado de un mensaje de GHL para pintar. Uno desconocido se pinta tal cual llega. */
export function etiquetaEstadoMensaje(estado: string): string {
  return Object.prototype.hasOwnProperty.call(ESTADOS_MENSAJE, estado) ? ESTADOS_MENSAJE[estado]! : estado;
}

const MOTIVOS: Record<string, string> = {
  no_messages_synced_in_range: 'Sin mensajes sincronizados en el rango.',
  no_ghl_location: 'Sin subcuenta de GHL vinculada.',
  no_ghl_api_key: 'Sin clave de API de GHL configurada.',
  ghl_workflows_failed: 'No se han podido leer las automatizaciones de GHL.',
};

/** Motivo de un bloque sin datos para pintar. Uno desconocido se pinta tal cual llega. */
export function etiquetaMotivo(motivo: string): string {
  return Object.prototype.hasOwnProperty.call(MOTIVOS, motivo) ? MOTIVOS[motivo]! : motivo;
}

/**
 * "Q3 2026" se pinta "T3 2026". Solo la etiqueta: el trimestre se sigue
 * calculando y pidiendo a la API con su año y su numero, nunca con este texto.
 */
export function etiquetaTrimestre(etiqueta: string): string {
  return etiqueta.replace(/\bQ([1-4])\b/g, 'T$1');
}

/** Un decimal con coma, al estilo español: 18.75 se pinta "18,8". */
export function unDecimal(n: number): string {
  return n.toFixed(1).replace('.', ',');
}

/** Un porcentaje (0..100) con una cifra decimal y el signo pegado sin partirse de linea: "36,2 %". */
export function porcentaje(n: number, decimales: 0 | 1 = 1): string {
  return `${decimales === 0 ? String(Math.round(n)) : unDecimal(n)}\u00a0%`;
}

/** Importe compacto en euros al estilo español: "186,4 k€", "1,2 M€", "950 €". */
export function eurosCompacto(n: number): string {
  if (n >= 1_000_000) return `${unDecimal(n / 1_000_000)} M€`;
  if (n >= 1_000) return `${unDecimal(n / 1_000)} k€`;
  return `${Math.round(n)} €`;
}
